import * as React from "react";
import { useLocalization } from "@shared-ui/localization-context";
import {
  DestinationDiscoveryMapModule,
  PointOfInterest,
  ThingToDo,
  Hotel
} from "typings/microserviceModels/destination-discovery-map-flex-module";
import { DestinationDiscoveryMapProps, MarkerCardContent, MapProps, Position } from "./typings";
import { MarkerIcon } from "./markers/MapMarkers";
import Map from "./Map";
import { MediaAttribution } from "components/utility/MediaAttributionUtil";
import { updateQueryStringParameter } from "components/utility/FeatureFlag/UrlUtils";
import { ExtendedContextStore } from "typings/flexFramework/FlexDefinitions";

export const DestinationDiscoveryMap = (props: DestinationDiscoveryMapProps) => {
  const content: DestinationDiscoveryMapModule = props.flexComponent.model?.content;
  const context: ExtendedContextStore = props.context;
  const noContent: boolean = !content || !Object.keys(content).length || props.flexComponent.model.empty;
  const destination = !noContent && content?.destination?.[0];
  const { formatText } = useLocalization();

  if (noContent || !destination) {
    return null;
  }

  const rfrrId = "TG.LP.Dest.Map";
  const pois = content.poi?.length && content.poi;
  const hotels = content.hotel?.length && content.hotel;
  const activities = content.activity?.length && content.activity;
  const staticMap = content.staticMapUrl;
  const poiMarkers =
    pois &&
    pois.map((poi: PointOfInterest) => {
      const cardContent: MarkerCardContent = {
        id: poi.id,
        type: MarkerIcon.POI,
        link: poi.link,
        photoUrl: poi.media?.mediaUrl,
        photoAlt: poi.media?.mediaAlt,
        attribution: poi.media && MediaAttribution(poi.media, poi.region.name),
        title: poi.region.name,
        description: poi.region.description,
        buttonLabel: formatText("destinationDiscoveryMap.button.label.poi"),
        buttonIcon: "search",
        rfrr: `${rfrrId}.POI`
      };
      const position: Position = { lat: poi.latitude, lng: poi.longitude };

      return {
        position,
        icon: MarkerIcon.POI,
        cardContent
      };
    });
  const hotelMarkers =
    hotels &&
    hotels.map((hotel: Hotel) => {
      const photoAlt = formatText("destinationDiscoveryMap.hotel.imageAlt", hotel.name);
      const cardContent: MarkerCardContent = {
        id: String(hotel.id),
        type: MarkerIcon.HOTEL,
        price: hotel.priceWithCurrencySymbol,
        link: hotel.url,
        photoUrl: hotel.images.sizeY,
        photoAlt,
        title: hotel.name,
        description: hotel.description,
        buttonLabel: formatText("destinationDiscoveryMap.button.label.hotel"),
        buttonIcon: "bed",
        rfrr: `${rfrrId}.Hotel`
      };

      return {
        position: { lat: hotel.coordinates.latitude, lng: hotel.coordinates.longitude },
        icon: MarkerIcon.HOTEL,
        cardContent
      };
    });
  const activityMarkers =
    activities &&
    activities.map((activity: ThingToDo) => {
      let image = activity.media?.mediaUrlTrimmed;
      if (image) {
        image = updateQueryStringParameter(image, "rw", "320");
        image = updateQueryStringParameter(image, "rh", null);
      }
      const cardContent: MarkerCardContent = {
        id: activity.id,
        type: MarkerIcon.ACTIVITY,
        price: activity.price,
        link: activity.link,
        photoUrl: image,
        photoAlt: activity.media?.mediaAlt,
        attribution: activity.media && MediaAttribution(activity.media, activity.name),
        title: activity.name,
        description: activity.description,
        buttonLabel: formatText("destinationDiscoveryMap.button.label.activity"),
        buttonIcon: "local_activity",
        rfrr: `${rfrrId}.Activity`
      };

      return {
        position: { lat: activity.latitude, lng: activity.longitude },
        icon: MarkerIcon.ACTIVITY,
        cardContent
      };
    });

  const mapProps: Omit<MapProps, "analytics" | "routeStore"> = { context, destination, staticMap };

  if (activityMarkers) {
    mapProps.activities = activityMarkers;
  }

  if (poiMarkers) {
    mapProps.pois = poiMarkers;
  }

  if (hotelMarkers) {
    mapProps.hotels = hotelMarkers;
  }

  return (
    <div
      className="DestinationDiscoveryMap all-cell-fill all-cell-1-1"
      id={props.flexComponent.id}
      data-fm={props.flexComponent.fmId}
      data-testid="destination-discovery-map-component"
    >
      <Map {...mapProps} />
    </div>
  );
};

export default DestinationDiscoveryMap;
